<template>
  <q-list :separator="separator" v-bind="$attrs">
    <file-item
      v-for="file in files"
      :key="file.url"
      :file="file"
      :can-delete="canDelete"
      @deleted="remove(file)"
    />
    <uploading-file-item
      v-for="file in uploading"
      :key="file.name"
      :file="file"
      @uploaded="onUpload"
    />
    <q-separator v-show="canUpload && separator && files.length > 0" />
    <select-file-item
      v-show="canUpload && (!this.maxFiles || (this.totalFiles < this.maxFiles))"
      :placeholder="uploadPlaceholder"
      :allowed-extensions="allowedExtensions"
      @selected="upload"
    />
  </q-list>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { UploadedFile } from '../type';
import FileItem from './FileItem.vue';
import UploadingFileItem from './upload/UploadingFileItem.vue';
import SelectFileItem from './upload/SelectFileItem.vue';

interface Data {
  uploading: File[]
}

export default defineComponent({
  emits: ['update:files'],
  components: { UploadingFileItem, SelectFileItem, FileItem },
  props: {
    files: {
      type: Array as () => UploadedFile[],
      required: true,
    },
    canUpload: {
      type: Boolean,
      default: false,
    },
    uploadPlaceholder: {
      type: String,
      default: 'Drag file here or click to upload.',
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    separator: {
      type: Boolean,
      default: false,
    },
    maxFiles: Number,
    allowedExtensions: Array as () => string[],
    validate: Function as PropType<(file: File) => Promise<boolean>>,
  },
  data(): Data {
    return {
      uploading: [],
    };
  },
  computed: {
    totalFiles(): number {
      return this.files.length + this.uploading.length;
    },
  },
  methods: {
    async keepValid(files: FileList): Promise<File[]> {
      if (!this.validate) return Array.from(files);
      const results = await Promise.all(
        Array.from(files).map(async (item) => {
          const keep = await this.validate(item);
          return { item, keep };
        }),
      );
      return results.filter(({ keep }) => keep).map(({ item }) => item);
    },
    async upload(files: FileList) {
      const validFiles = await this.keepValid(files);
      this.uploading = [...this.uploading, ...validFiles];
    },
    onUpload(file: UploadedFile) {
      const index = this.uploading.findIndex(f => f.name === file.name);
      if (index >= 0) {
        this.uploading.splice(index, 1);
      }
      this.$emit('update:files', [...this.files, file]);
    },
    remove(file: UploadedFile) {
      const index = this.files.findIndex((f => f.url === file.url));
      if (index >= 0) {
        const files = [...this.files];
        files.splice(index, 1);
        this.$emit('update:files', files);
      }
    },
  },
});
</script>
